// Age-group benchmark data for skin metrics
// Used to compare user's scores against their age group average

import type { MetricKey } from '@/types/analysis'

// Age group ranges
export type AgeGroup = '16-24' | '25-34' | '35-44' | '45-54' | '55+'

// Benchmark scores by age group (0-100 scale, higher = better skin condition)
// Based on dermatological research and skincare industry standards
export const AGE_BENCHMARKS: Record<AgeGroup, Record<MetricKey, number>> = {
  '16-24': {
    pores: 58,
    wrinkles: 88,
    texture: 72,
    acne: 55,          // Lower skin health for acne in younger ages
    redness: 65,
    darkCircles: 70,
    eyeBags: 80,
    spots: 75,
    oiliness: 45,      // Lower oiliness score in younger ages
    moisture: 75,      // Higher = better (more moisture)
    firmness: 85,      // Higher = better (more firm)
    radiance: 78,      // Higher = better (more radiant)
    droopyEyelidUpper: 90,
    droopyEyelidLower: 92,
  },
  '25-34': {
    pores: 50,
    wrinkles: 78,
    texture: 65,
    acne: 65,
    redness: 62,
    darkCircles: 60,
    eyeBags: 70,
    spots: 65,
    oiliness: 55,
    moisture: 68,
    firmness: 75,
    radiance: 70,
    droopyEyelidUpper: 82,
    droopyEyelidLower: 85,
  },
  '35-44': {
    pores: 45,
    wrinkles: 62,
    texture: 58,
    acne: 75,
    redness: 60,
    darkCircles: 52,
    eyeBags: 58,
    spots: 55,
    oiliness: 62,
    moisture: 60,
    firmness: 62,
    radiance: 60,
    droopyEyelidUpper: 70,
    droopyEyelidLower: 72,
  },
  '45-54': {
    pores: 42,
    wrinkles: 48,
    texture: 52,
    acne: 82,
    redness: 58,
    darkCircles: 48,
    eyeBags: 50,
    spots: 45,
    oiliness: 68,
    moisture: 50,
    firmness: 48,
    radiance: 52,
    droopyEyelidUpper: 55,
    droopyEyelidLower: 58,
  },
  '55+': {
    pores: 40,
    wrinkles: 35,
    texture: 48,
    acne: 88,
    redness: 55,
    darkCircles: 45,
    eyeBags: 45,
    spots: 40,
    oiliness: 72,
    moisture: 42,
    firmness: 38,
    radiance: 45,
    droopyEyelidUpper: 45,
    droopyEyelidLower: 48,
  },
}

// Get age group from numeric age
export function getAgeGroup(age: number): AgeGroup {
  if (age < 25) return '16-24'
  if (age < 35) return '25-34'
  if (age < 45) return '35-44'
  if (age < 55) return '45-54'
  return '55+'
}

// Get benchmark for a specific metric and age
export function getBenchmark(age: number, metric: MetricKey): number {
  const ageGroup = getAgeGroup(age)
  return AGE_BENCHMARKS[ageGroup][metric]
}

// Get all benchmarks for a specific age
export function getAllBenchmarks(age: number): Record<MetricKey, number> {
  const ageGroup = getAgeGroup(age)
  return AGE_BENCHMARKS[ageGroup]
}

// Calculate how user compares to their age group
// Returns: positive = better than average, negative = worse than average
export function getComparisonDiff(userScore: number, age: number, metric: MetricKey): number {
  const benchmark = getBenchmark(age, metric)
  return userScore - benchmark
}

// Get comparison text for UI
export function getComparisonText(userScore: number, age: number, metric: MetricKey): string {
  const diff = getComparisonDiff(userScore, age, metric)
  const absDiff = Math.abs(Math.round(diff))

  if (diff >= 15) return `${absDiff}% better than average`
  if (diff >= 5) return `${absDiff}% better than average`
  if (diff > -5) return 'About average'
  if (diff > -15) return `${absDiff}% lower than average`
  return `${absDiff}% lower than average`
}
